import { useEffect, useState } from 'react'
import { useQuery } from 'urql'
import type { ActivityJson } from 'app/types/entities'
import type { AppointmentJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import type { TransactionJson } from 'app/types/entities'
import type { ProcedureJson, TreatmentPlanJson } from 'app/types/entities'
import type { PlanWithLines } from 'app/types/treatment'
import { ACTIVITIES_QUERY } from 'app/stores/ActivityStore'
import {
  useAppointmentStore,
  useProcedureStore,
  useTransactionStore,
  useTreatmentPlanStore,
} from 'app/stores/StoreContext'
import { TASKS_QUERY } from 'app/stores/TaskStore'
import { bg } from 'lib/background'
import { run } from 'lib/run'
import { attachLines } from 'lib/treatmentPlanLines'

type Totals = {
  all?: number
  tasks?: number
  appointments?: number
  transactions?: number
  treatmentPlans?: number
}

/** Every procedure a patient has, in any state, since a plan can carry completed and deleted lines too. */
const PROCEDURE_READ_LIMIT = 1000

const emptyTotals: Totals = { all: 0, tasks: 0, appointments: 0, transactions: 0, treatmentPlans: 0 }

export function buildPatientIdSearch(patientIds: string[] | undefined | null): string {
  if (!patientIds || patientIds.length === 0) return ''
  return `patientId:(${patientIds.join(' OR ')})`
}

export function useContactRelatedObjects(contact?: ContactJson) {
  const appointmentStore = useAppointmentStore()
  const transactionStore = useTransactionStore()
  const treatmentPlanStore = useTreatmentPlanStore()
  const procedureStore = useProcedureStore()

  const [activities, setActivities] = useState<ActivityJson[]>()
  const [appointments, setAppointments] = useState<AppointmentJson[]>()
  const [transactions, setTransactions] = useState<TransactionJson[]>()
  const [treatmentPlans, setTreatmentPlans] = useState<PlanWithLines[]>()
  const [totals, setTotals] = useState<Totals>(emptyTotals)
  const contactSearch = contact
    ? `workspaceId:${contact.workspaceId} locationId:${contact.locationId} contacts.id.keyword:${contact.id}`
    : undefined
  const [activitiesResult, reexecuteActivities] = useQuery({
    query: ACTIVITIES_QUERY,
    variables: {
      search: contactSearch,
      sort: { field: `activityDate`, direction: `DESC` },
      pagination: { limit: 1000 },
    },
    pause: !contact,
  })
  const [tasksResult, reexecuteTasks] = useQuery({
    query: TASKS_QUERY,
    variables: {
      search: `contactId:${contact?.id}`,
      sort: { field: `createdDate`, direction: `DESC` },
      pagination: { limit: 1000 },
    },
    pause: !contact,
  })
  const activityHits = activitiesResult.data?.activities?.hits
  const taskHits = tasksResult.data?.tasks?.hits
  // urql keeps the previous variables' data while the next request is in flight, so a
  // contact switch or a refresh must wait for fetching to settle before replacing the list.
  const isActivityListSettled =
    !!contact && !activitiesResult.fetching && !tasksResult.fetching && !!activityHits && !!taskHits

  useEffect(() => {
    setActivities(undefined)
    setAppointments(undefined)
    setTransactions(undefined)
    setTreatmentPlans(undefined)
    setTotals(emptyTotals)
  }, [contact?.id])

  // Keyed on the id too: a host that blanks and refetches the same contact gets identical
  // cached hits back, so the hits alone never re-run this after the reset above.
  useEffect(() => {
    if (!isActivityListSettled) return
    const acts = activityHits as ActivityJson[]
    setActivities([
      ...acts,
      ...taskHits.map(
        (task) =>
          ({
            id: task.id,
            activityDate: task.createdDate,
            type: `Task`,
            data: { dueDate: task.dueDate, status: task.status },
          }) as ActivityJson,
      ),
    ])
    setTotals((prev) => ({
      ...prev,
      tasks: taskHits.length,
      all: acts.filter((a) => a.type !== `Appointment` && a.type !== `Change`).length + taskHits.length,
    }))
  }, [contact?.id, isActivityListSettled, activityHits, taskHits])

  useEffect(() => {
    if (!contact) return
    let cancelled = false

    bg(async () => {
      await Promise.all([
        run(async () => {
          const patientSearch = buildPatientIdSearch(contact.patientIds)
          if (!patientSearch) return
          const result = await appointmentStore.fetchList({
            search: patientSearch,
            sort: { field: `date`, direction: `DESC` },
            pagination: { limit: 1000 },
          })
          const appts = ((result?.data as any)?.appointments?.hits ?? []) as AppointmentJson[]
          if (cancelled) return
          setAppointments(appts)
          setTotals((prev) => ({ ...prev, appointments: appts?.length ?? 0 }))
        }),
        run(async () => {
          const patientSearch = buildPatientIdSearch(contact.patientIds)
          if (!patientSearch) return
          const result = await transactionStore.fetchList({
            search: patientSearch,
            sort: { field: `transactionDate`, direction: `DESC` },
            pagination: { limit: 1000 },
          })
          const txs = ((result?.data as any)?.transactions?.hits ?? []) as TransactionJson[]
          if (cancelled) return
          setTransactions(txs)
          setTotals((prev) => ({ ...prev, transactions: txs?.length ?? 0 }))
        }),
        run(async () => {
          const patientSearch = buildPatientIdSearch(contact.patientIds)
          if (!patientSearch) return
          const [planResult, procedureResult] = await Promise.all([
            treatmentPlanStore.fetchList({
              search: patientSearch,
              sort: { field: `planCreateDate`, direction: `DESC` },
              pagination: { limit: 1000 },
            }),
            procedureStore.fetchList({ search: patientSearch, pagination: { limit: PROCEDURE_READ_LIMIT } }),
          ])
          const plans = ((planResult?.data as any)?.treatmentPlans?.hits ?? []) as TreatmentPlanJson[]
          const procedures = ((procedureResult?.data as any)?.procedures?.hits ?? []) as ProcedureJson[]
          if (cancelled) return
          setTreatmentPlans(attachLines(plans, procedures))
          setTotals((prev) => ({ ...prev, treatmentPlans: plans?.length ?? 0 }))
        }),
      ])
    })

    return () => {
      cancelled = true
    }
  }, [contact?.id])

  const handleActivityAdded = (activity: ActivityJson) => {
    setActivities((prev) => [activity, ...(prev ?? [])])
    setTotals((prev) => ({ ...prev, all: (prev.all ?? 0) + 1 }))
  }

  const handleActivitiesChange = (newActivities: ActivityJson[]) => {
    setActivities(newActivities)
    const activityCount = newActivities?.filter((a) => a.type !== `Appointment` && a.type !== `Change`)?.length ?? 0
    setTotals((prev) => ({ ...prev, all: activityCount }))
  }

  // Saving a contact writes its Change activity on the server, and the default
  // cache-first policy would hand back the list fetched when the drawer opened.
  const refreshActivities = () => {
    reexecuteActivities({ requestPolicy: `network-only` })
    reexecuteTasks({ requestPolicy: `network-only` })
  }

  return {
    activities,
    appointments,
    transactions,
    treatmentPlans,
    totals,
    handleActivityAdded,
    handleActivitiesChange,
    refreshActivities,
  }
}
